/**
 * NorthstarBreadcrumb — persistent Northstar navigation chrome that
 * renders on destination pages after a member arrives via an orb.
 *
 * Trigger: `?from=northstar&orb=<key>` is present on the current URL,
 * OR sessionStorage has `northstar:origin` set (survives if the
 * destination strips its own query params).
 *
 * Responsibilities (Phase 2A item 1):
 *   - Clear current-location indicator ("NORTHSTAR › {Orb Label}")
 *   - Reliable, always-available "Return to Northstar" action
 *   - Feels like part of the deep-space environment, not a generic
 *     breadcrumb bar
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ORBS } from "../../constants/northstarOrbs";

export default function NorthstarBreadcrumb() {
  const location = useLocation();
  const navigate = useNavigate();
  const [orbKey, setOrbKey] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let key = params.get("orb");
    if (params.get("from") !== "northstar") key = null;
    if (!key) {
      try { key = sessionStorage.getItem("northstar:origin"); } catch {}
    }
    setOrbKey(key);
  }, [location.search, location.pathname]);

  if (!orbKey) return null;

  const orb = ORBS.find((o) => o.key === orbKey);
  if (!orb) return null;

  const handleReturn = (e) => {
    e.preventDefault();
    try { sessionStorage.removeItem("northstar:origin"); } catch {}
    navigate("/northstar");
  };

  return (
    <div
      className="sticky top-0 z-30"
      data-testid="northstar-breadcrumb"
      data-orb={orbKey}
    >
      <div
        className="w-full text-white"
        style={{
          background:
            "linear-gradient(180deg, rgba(4,8,22,0.98) 0%, rgba(4,8,22,0.92) 100%)",
          borderBottom: "1px solid rgba(126,212,147,0.22)",
          boxShadow: "0 8px 24px rgba(4,8,22,0.35)",
        }}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div
            className="flex items-center gap-2 min-w-0"
            data-testid="northstar-breadcrumb-trail"
          >
            <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#7ED493] flex-shrink-0">
              Northstar
            </span>
            <span aria-hidden="true" className="text-white/40 flex-shrink-0">›</span>
            <span className="text-sm font-semibold tracking-tight truncate">
              {orb.label}
            </span>
          </div>
          <Link
            to="/northstar"
            onClick={handleReturn}
            data-testid="northstar-breadcrumb-return"
            className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs uppercase tracking-[0.2em] text-white/85 hover:text-white active:text-white transition-colors flex-shrink-0 py-1.5 px-2 -mr-2 rounded"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Return to </span>Northstar
          </Link>
        </div>
      </div>
    </div>
  );
}
